import {
  AppointmentStatus,
  IFormField,
  LeadStatus,
  FormFieldType,
  ServiceStatus,
  TaskStatus,
  AppointmentType,
  IStatus,
  UserRole,
  IRole,
  CampaignStatus,
  LabReqTestOption,
  IConsultationStatus,
  OrderStatus,
  OrderStatusKey,
  OrderSource,
  DrugStatus,
  WorkflowRunStatus,
  ISubscriptionStatus,
  PaymentStatusKey
} from '@care-portals/api-interfaces';

import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  sub
} from 'date-fns';

export const AppointmentStatuses: {
  [key in AppointmentStatus]: IStatus;
} = {
  new: { label: 'New', color: 'orange' },
  completed: { label: 'Completed', color: 'green' },
  cancelled: { label: 'Cancelled', color: 'red' },
  confirmed: { label: 'Confirmed', color: 'blue' },
  'in-progress': { label: 'In Progress', color: 'blue' },
  waiting: { label: 'Waiting', color: 'blue' },
  'no-show': { label: 'No Show', color: 'gray' },
  'pending-input': { label: 'Pending Input', color: 'orange' },
  overflow: { label: 'Overflow', color: 'yellow' },
  closed: { label: 'Closed (No Charge)', color: 'yellow' }
};

export const AppointmentTypes: {
  [key in AppointmentType]: { label: string };
} = {
  Video: { label: 'Video' },
  Physical: { label: 'Physical' },
  Phone: { label: 'Phone' }
};

export const ServicesStatuses: {
  [key in ServiceStatus]: IStatus;
} = {
  active: { label: 'Active', color: 'green' },
  disabled: { label: 'Disabled', color: 'gray' }
};

export const DrugStatuses: {
  [key in DrugStatus]: IStatus;
} = {
  active: { label: 'Active', color: 'green' },
  disabled: { label: 'Disabled', color: 'gray' }
};

export const WorkflowRunsStatuses: {
  [key in WorkflowRunStatus]: IStatus;
} = {
  running: { label: 'Running', color: 'yellow' },
  failed: { label: 'Failed', color: 'red' },
  completed: { label: 'Completed', color: 'green' },
  idle: { label: 'Idle', color: 'gray' }
};

export const CampaignStatuses: {
  [key in CampaignStatus]: IStatus;
} = {
  new: { label: 'New', color: 'orange' },
  launched: { label: 'Launched', color: 'blue' }
};

export const LeadStatuses: {
  [key in LeadStatus]: IStatus;
} = {
  new: { label: 'New', color: 'orange' },
  working: { label: 'Working', color: 'blue' },
  lost: { label: 'Lost', color: 'blue' },
  unqualified: { label: 'Unqualified', color: 'gray' },
  converted: { label: 'Converted', color: 'green' },
  archived: { label: 'Archived', color: 'gray' }
};

export const FiledTypes: {
  [key in FormFieldType]: {
    label: string;
    order: number;
    icon: string;
    disabled?: boolean;
    variants?: string[];
  };
} = {
  text: { label: 'Text', order: 0, icon: 'font-size' },
  area: { label: 'Text area', order: 1, icon: 'align-left' },
  email: { label: 'Email', order: 2, icon: 'mail' },
  date: { label: 'Date', order: 3, icon: 'calendar', variants: ['dob'] },
  section: { label: 'Section', order: 4, icon: 'pic-left' },
  file: { label: 'File', order: 5, icon: 'file' },
  number: { label: 'Number', order: 6, icon: 'number' },
  // @Deprecated
  phone: { label: 'Phone', order: 6, icon: 'phone', disabled: true },
  phone164: { label: 'Phone', order: 6, icon: 'phone' },
  consent: {
    label: 'Consent',
    order: 7,
    icon: 'check-square',
    variants: ['switch']
  },
  switch: { label: 'Switch', order: 7, icon: 'control' },
  polar: { label: 'Polar (yes/no)', order: 8, icon: 'apartment' },
  singleselect: { label: 'Single-Select', order: 9, icon: 'select' },
  multiselect: { label: 'Multi-Select', order: 10, icon: 'ungroup' },
  ontario_hc: { label: 'Ontario HC', order: 11, icon: 'idcard' },
  creditcard: { label: 'Credit Card', order: 12, icon: 'credit-card' },
  group: { label: 'Fields Group', order: 13, icon: 'group' }
};

export const TaskStatuses: {
  [key in TaskStatus]: IStatus;
} = {
  new: { label: 'New', color: 'orange' },
  inprogress: { label: 'In-Progress', color: 'blue' },
  completed: { label: 'Completed', color: 'green' },
  cancelled: { label: 'Cancelled', color: 'gray' },
  pending: { label: 'Pending', color: 'yellow' }
};

export enum InsuranceType {
  OHIP = 'OHIP',
  ThirdParty = 'ThirdParty',
  WSIB = 'WSIB'
}
export const insuranceTypeFields: Array<LabReqTestOption> = [
  { type: 'boolean', id: InsuranceType.OHIP, label: 'OHIP/Insured' },
  {
    type: 'boolean',
    id: InsuranceType.ThirdParty,
    label: 'Third Party / Uninsured'
  },
  { type: 'boolean', id: InsuranceType.WSIB, label: 'WSIB' }
];

export const DEFAULT_SERVICE_FORM_FIELDS: IFormField[] = [
  {
    label: 'First Name',
    name: 'firstName',
    required: true,
    type: 'text'
  },
  {
    label: 'Last Name',
    name: 'lastName',
    required: true,
    type: 'text'
  },
  {
    label: 'Date of Birth',
    name: 'dob',
    required: true,
    type: 'date'
  },
  {
    label: 'Phone Number',
    name: 'phone',
    required: false,
    type: 'text'
  },
  {
    label: 'Email Address',
    name: 'email',
    required: true,
    type: 'email',
    mandatory: true
  },
  {
    label: 'Street Address',
    name: 'address',
    required: true,
    type: 'text'
  },
  {
    label: 'Postal Code',
    name: 'postal',
    required: true,
    type: 'text'
  },

  /* ------------------------------- Health Card ------------------------------ */

  {
    label: 'Health Card',
    name: 'healthCardSection',
    type: 'section'
  },
  {
    label: 'Number',
    name: 'hcNum',
    required: false,
    type: 'text'
  },
  {
    label: '2 Letter Version Code',
    name: 'hcVer',
    required: false,
    type: 'text'
  },
  {
    label: 'Effective',
    name: 'hcEff',
    required: false,
    type: 'date'
  },
  {
    label: 'Expiry',
    name: 'hcExp',
    required: false,
    type: 'date'
  }
];

export const getDateRanges = () => {
  const now = new Date();
  return {
    Today: [startOfDay(now), endOfDay(now)],
    'Last 7 Days': [startOfDay(sub(now, { days: 7 })), endOfDay(now)],
    'This Week': [startOfWeek(now), endOfWeek(now)],
    'This Month': [startOfMonth(now), endOfMonth(now)]
  };
};

export const USERS_ROLES: { [key in UserRole]: IRole } = {
  user: { label: 'Staff' },
  admin: { label: 'Admin' },
  developer: { label: 'Developer' },
  pharmacy: { label: 'Pharmacy' },
  provider: { label: 'Health Provider', isHidden: true },
  'provider-admin': { label: 'Provider/Admin', isHidden: true },
  owner: { label: 'Owner' },
  'pharmacy-admin': { label: 'Pharmacy/Admin' },
  marketing: { label: 'Marketing' },
  /**
   * Only allows access to EMR, no frontend access to CRM
   */
  support: { label: 'Support' }
  // doctor: { label: 'Doctor' },
  // np: { label: 'Nurse Practitioner' }
};

export const countries = {
  US: 'United States',
  CA: 'Canada'
};

export const caProvinces = [
  { code: 'ON', province: 'Ontario' },
  { code: 'QC', province: 'Quebec' },
  { code: 'AB', province: 'Alberta' },
  { code: 'BC', province: 'British Columbia' },
  { code: 'MB', province: 'Manitoba' },
  { code: 'NL', province: 'Newfoundland and Labrador' },
  { code: 'PE', province: 'Prince Edward Island' },
  { code: 'NS', province: 'Nova Scotia' },
  { code: 'NB', province: 'New Brunswick' },
  { code: 'SK', province: 'Saskatchewan' },
  { code: 'YT', province: 'Yukon Territory' },
  { code: 'NT', province: 'Northwest Territories' },
  { code: 'NU', province: 'Nunavut' },
  { code: 'OTHER', province: 'Other' }
];

export const usStates = [
  { code: 'AK', province: 'Alaska' },
  { code: 'AZ', province: 'Arizona' },
  { code: 'AL', province: 'Alabama' },
  { code: 'AR', province: 'Arkansas' },
  { code: 'CA', province: 'California' },
  { code: 'CO', province: 'Colorado' },
  { code: 'CT', province: 'Connecticut' },
  { code: 'DC', province: 'District of Columbia' },
  { code: 'DE', province: 'Delaware' },
  { code: 'FL', province: 'Florida' },
  { code: 'GA', province: 'Georgia' },
  { code: 'HI', province: 'Hawaii' },
  { code: 'ID', province: 'Idaho' },
  { code: 'IL', province: 'Illinois' },
  { code: 'IN', province: 'Indiana' },
  { code: 'IA', province: 'Iowa' },
  { code: 'KS', province: 'Kansas' },
  { code: 'KY', province: 'Kentucky' },
  { code: 'LA', province: 'Louisiana' },
  { code: 'ME', province: 'Maine' },
  { code: 'MD', province: 'Maryland' },
  { code: 'MA', province: 'Massachusetts' },
  { code: 'MI', province: 'Michigan' },
  { code: 'MN', province: 'Minnesota' },
  { code: 'MS', province: 'Mississippi' },
  { code: 'MO', province: 'Missouri' },
  { code: 'MT', province: 'Montana' },
  { code: 'NE', province: 'Nebraska' },
  { code: 'NV', province: 'Nevada' },
  { code: 'NH', province: 'New Hampshire' },
  { code: 'NJ', province: 'New Jersey' },
  { code: 'NM', province: 'New Mexico' },
  { code: 'NY', province: 'New York' },
  { code: 'NC', province: 'North Carolina' },
  { code: 'ND', province: 'North Dakota' },
  { code: 'OH', province: 'Ohio' },
  { code: 'OK', province: 'Oklahoma' },
  { code: 'OR', province: 'Oregon' },
  { code: 'PA', province: 'Pennsylvania' },
  { code: 'RI', province: 'Rhode Island' },
  { code: 'SC', province: 'South Carolina' },
  { code: 'SD', province: 'South Dakota' },
  { code: 'TN', province: 'Tennessee' },
  { code: 'TX', province: 'Texas' },
  { code: 'UT', province: 'Utah' },
  { code: 'VT', province: 'Vermont' },
  { code: 'VA', province: 'Virginia' },
  { code: 'WA', province: 'Washington' },
  { code: 'WV', province: 'West Virginia' },
  { code: 'WI', province: 'Wisconsin' },
  { code: 'WY', province: 'Wyoming' },
  { code: 'OTHER', province: 'Other' }
];
export const Genders = {
  '': { label: '- - -' },
  M: { label: 'Male' },
  F: { label: 'Female' }
};

// token expiry used by chat service
export const TOKEN_TTL_IN_SECONDS = 60 * 60 * 6;

// Bigcommerce Order status numbers as defined by bigcommerce
export enum OrderStatusIds {
  Incomplete,
  Pending,
  Shipped,
  PartiallyShipped,
  Refunded,
  Cancelled,
  Declined,
  AwaitingPayment,
  AwaitingPickup,
  AwaitingShipment,
  Completed,
  AwaitingFulfillment,
  ManualVerificationRequired,
  Disputed,
  PartiallyRefunded,
  Rejected
}

// use to convert the bigcommerce status ID to mongo status keys
export const ORDER_STATUS_KEYS: Record<OrderStatusIds, OrderStatusKey> = {
  [OrderStatusIds.Incomplete]: 'incomplete',
  [OrderStatusIds.Pending]: 'pending',
  [OrderStatusIds.Shipped]: 'shipped',
  [OrderStatusIds.PartiallyShipped]: 'partially_shipped',
  [OrderStatusIds.Refunded]: 'refunded',
  [OrderStatusIds.Cancelled]: 'cancelled',
  [OrderStatusIds.Declined]: 'declined',
  [OrderStatusIds.AwaitingPayment]: 'awaiting_payment',
  [OrderStatusIds.AwaitingPickup]: 'awaiting_pickup',
  [OrderStatusIds.AwaitingShipment]: 'awaiting_shipment',
  [OrderStatusIds.Completed]: 'completed',
  [OrderStatusIds.AwaitingFulfillment]: 'processing',
  [OrderStatusIds.ManualVerificationRequired]: 'manual_verification_required',
  [OrderStatusIds.Disputed]: 'disputed',
  [OrderStatusIds.PartiallyRefunded]: 'partially_refunded',
  [OrderStatusIds.Rejected]: 'rejected'
};

export type PaymentStatus = {
  name: string;
  icon: string;
  color: string;
  show: boolean;
  sort: number;
};

export const PAYMENT_STATUSES: { [key in PaymentStatusKey]: PaymentStatus } = {
  pending: {
    name: 'Pending',
    icon: 'fas fa-clock',
    color: '#f59e0b', // amber-500
    show: true,
    sort: 1
  },
  adjudicated: {
    name: 'Adjudicated',
    icon: 'fas fa-clipboard-check',
    color: '#f59e0b', // amber-500
    show: true,
    sort: 2
  },
  processing: {
    name: 'Processing',
    icon: 'fas fa-spinner',
    color: '#8b5cf6', // violet-500
    show: true,
    sort: 3
  },
  requires_action: {
    name: 'Requires Action',
    icon: 'fas fa-exclamation-triangle',
    color: '#f59e0b', // amber-500
    show: true,
    sort: 4
  },
  voided: {
    name: 'Voided',
    icon: 'fas fa-ban',
    color: '#6b7280', // gray-500
    show: true,
    sort: 5
  },
  authorized: {
    name: 'Authorized',
    icon: 'fas fa-shield',
    color: '#1e40af', // blue-800
    show: true,
    sort: 6
  },
  failed: {
    name: 'Failed',
    icon: 'fas fa-times-circle',
    color: '#ef4444', // red-500
    show: true,
    sort: 7
  },

  paid: {
    name: 'Paid',
    icon: 'fas fa-check-circle',
    color: '#22c55e', // green-500
    show: true,
    sort: 8
  },
  partially_refunded: {
    name: 'Partially Refunded',
    icon: 'fas fa-undo',
    color: '#14b8a6', // teal-500
    show: true,
    sort: 9
  },
  refunded: {
    name: 'Refunded',
    icon: 'fas fa-undo',
    color: '#3b82f6', // blue-500
    show: true,
    sort: 10
  },
  delayed_capture: {
    name: 'Delayed Capture',
    icon: 'fas fa-pause-circle',
    color: '#6366f1', // indigo-500
    show: true,
    sort: 11
  },
  partial_paid: {
    name: 'Partially Paid',
    icon: 'fas fa-circle-half-stroke',
    color: '#8b5cf6', // violet-500
    show: true,
    sort: 11.5
  },
  skipped: {
    name: 'Skipped',
    icon: 'fas fa-times-circle',
    color: '#6b7280', // gray-500
    show: true,
    sort: 12
  }
};

export const ORDER_STATUSES: { [key in OrderStatusKey]: OrderStatus } = {
  incomplete: {
    name: 'Incomplete',
    statsName: 'Incomplete',
    icon: 'fas fa-circle-stop',
    show: true,
    id: OrderStatusIds.Incomplete,
    sort: 10,
    stage: 'placement'
  },
  pending: {
    name: 'Pending',
    statsName: 'Pending',
    icon: 'fas fa-hourglass-half',
    show: true,
    id: OrderStatusIds.Pending,
    sort: 20,
    stage: 'placement'
  },
  awaiting_payment: {
    name: 'Awaiting Payment',
    statsName: 'Payment',
    icon: 'fas fa-hand-holding-dollar',
    show: true,
    id: OrderStatusIds.AwaitingPayment,
    sort: 30,
    stage: 'placement'
  },
  processing: {
    name: 'Processing',
    statsName: 'Processing',
    icon: 'fas fa-spinner',
    show: true,
    id: OrderStatusIds.AwaitingFulfillment,
    sort: 40,
    stage: 'placement'
  },
  awaiting_requirements: {
    name: 'Awaiting Requirements',
    statsName: 'Requirements',
    icon: 'fas fa-list-check',
    show: true,
    id: OrderStatusIds.AwaitingFulfillment,
    sort: 50,
    stage: 'authorization'
  },
  // provider statuses
  awaiting_requisition: {
    name: 'Awaiting Requisition',
    statsName: 'Requisition',
    icon: 'fas fa-syringe',
    show: true,
    id: OrderStatusIds.AwaitingFulfillment,
    sort: 60,

    stage: 'authorization'
  },
  awaiting_results: {
    name: 'Awaiting Results',
    statsName: 'Results',
    icon: 'fas fa-print',
    show: true,
    id: OrderStatusIds.AwaitingFulfillment,
    sort: 70,
    stage: 'authorization'
  },
  awaiting_script: {
    name: 'Awaiting Script',
    statsName: 'Script',
    icon: 'fas fa-prescription',
    show: true,
    id: OrderStatusIds.AwaitingFulfillment,
    sort: 80,
    stage: 'authorization'
  },
  // phamarcy statuses
  awaiting_fulfillment: {
    name: 'Awaiting Fulfillment',
    statsName: 'Fulfillment',
    icon: 'fas fa-capsules',
    show: true,
    id: OrderStatusIds.AwaitingFulfillment,
    sort: 90,
    stage: 'fulfillment'
  },
  awaiting_shipment: {
    name: 'Awaiting Shipment',
    statsName: 'Shipment',
    icon: 'fas fa-box-open',
    show: true,
    id: OrderStatusIds.AwaitingShipment,
    sort: 100,
    stage: 'fulfillment'
  },
  // carrier statuses
  awaiting_pickup: {
    name: 'Awaiting Pickup',
    statsName: 'Pickup',
    icon: 'fas fa-spinner',
    show: false,
    id: OrderStatusIds.AwaitingPickup,
    sort: 110,
    stage: 'delivery'
  },
  shipped: {
    name: 'Shipped',
    statsName: 'Shipped',
    icon: 'fas fa-truck-fast',
    show: true,
    id: OrderStatusIds.Shipped,
    sort: 120,
    stage: 'delivery'
  },
  completed: {
    name: 'Completed',
    statsName: 'Completed',
    icon: 'fas fa-square-check',
    show: true,
    id: OrderStatusIds.Completed,
    sort: 130,
    stage: 'closure'
  },
  returned: {
    name: 'Returned',
    statsName: 'Returned',
    icon: 'fas fa-box',
    show: true,
    id: OrderStatusIds.AwaitingShipment,
    sort: 135,
    stage: 'fulfillment'
  },
  cancelled: {
    name: 'Cancelled',
    statsName: 'Cancelled',
    icon: 'fas fa-square-xmark',
    show: true,
    id: OrderStatusIds.Cancelled,
    sort: 140,
    stage: 'closure'
  },
  declined: {
    name: 'Declined',
    statsName: 'Declined',
    icon: 'fas fa-spinner',
    show: false,
    id: OrderStatusIds.Declined,
    sort: 150,
    stage: 'closure'
  },
  disputed: {
    name: 'Disputed',
    statsName: 'Disputed',
    icon: 'fas fa-spinner',
    show: false,
    id: OrderStatusIds.Disputed,
    sort: 160,
    stage: 'closure'
  },
  // hidden statuses
  refunded: {
    name: 'Refunded',
    statsName: 'Refunded',
    icon: 'fas fa-spinner',
    show: false,
    id: OrderStatusIds.Refunded,
    sort: 170
  },
  partially_shipped: {
    name: 'Partially Shipped',
    statsName: 'Partially Shipped',
    icon: 'fas fa-spinner',
    show: false,
    id: OrderStatusIds.PartiallyShipped,
    sort: 180
  },
  manual_verification_required: {
    name: 'Manual Verification Required',
    statsName: 'Manual Verification Required',
    icon: 'fas fa-spinner',
    show: false,
    id: OrderStatusIds.ManualVerificationRequired,
    sort: 190
  },
  partially_refunded: {
    name: 'Partially Refunded',
    statsName: 'Partially Refunded',
    icon: 'fas fa-spinner',
    show: false,
    id: OrderStatusIds.PartiallyRefunded,
    sort: 200
  },
  rejected: {
    name: 'Rejected',
    statsName: 'Rejected',
    icon: 'fas fa-square-xmark',
    show: true,
    id: OrderStatusIds.Rejected,
    sort: 210,
    stage: 'closure'
  }
};

export const ORDER_SOURCES: { name: OrderSource; label: string }[] = [
  { name: 'checkout', label: 'Checkout' },
  { name: 'subscription_cycle', label: 'Subscription Cycle' },
  { name: 'manual', label: 'Manual' },
  { name: 'manual_refill', label: 'Manual Refill' }
];

export const formStatuses: { name: IConsultationStatus; label: string }[] = [
  {
    name: 'incomplete',
    label: 'Incomplete'
  },
  {
    name: 'new',
    label: 'New - Awaiting Review'
  },
  {
    name: 'pending-input',
    label: 'Pending Input'
  },
  {
    name: 'inprogress',
    label: 'Under Review'
  },
  {
    name: 'wating-for-labwork',
    label: 'Waiting for Labwork'
  },
  {
    name: 'lab-results-in',
    label: 'Lab Results In'
  },
  {
    name: 'ready-for-np',
    label: 'Ready for NP'
  },
  {
    name: 'completed',
    label: 'Reviewed'
  },
  {
    name: 'cancelled',
    label: 'Cancelled'
  },
  {
    name: 'rejected',
    label: 'Rejected'
  },
  {
    name: 'archived',
    label: 'Archived'
  }
];

export const QUESTIONNAIRE_STATUSES = formStatuses.reduce(
  (a, v) => ({ ...a, [v.name]: v.label }),
  {}
);

export const SUBSCRIPTION_STATUSES: {
  name: ISubscriptionStatus;
  label: string;
}[] = [
  { name: 'active', label: 'Active' },
  { name: 'cancelled', label: 'Cancelled' },
  { name: 'paused', label: 'Paused' },
  { name: 'ended', label: 'Ended' }
];

export const groupsList = ['Pharmacy', 'Customer Support', 'Health Providers'];

export const eventBridgeTypes = {
  prescriptions: {
    fulfillment: 'fulfillment',
    created: 'created'
  }
};

export const CANNED_MESSAGE_PLACEHOLDERS = [
  { value: 'customerFirstName', label: 'Customer First Name' },
  { value: 'customerLastName', label: 'Customer Last Name' },
  { value: 'customerFullName', label: 'Customer Full Name' },
  { value: 'agentFirstName', label: 'Agent First Name' },
  { value: 'agentLastName', label: 'Agent Last Name' },
  { value: 'agentFullName', label: 'Agent Full Name' }
];

export const VERIFICATION_REQS = {
  idVerification: {
    name: 'ID Verification',
    documentType: 'govId'
  },
  drugCardVerification: {
    name: 'Drug Card Verification',
    documentType: 'drugCard'
  },
  age18Verification: {
    name: 'Age Verification 18+',
    age: 18,
    documentType: 'govId'
  },
  age21Verification: {
    name: 'Age Verification 21+',
    age: 21,
    documentType: 'govId'
  },
  age16Verification: {
    name: 'Age Verification 16+',
    age: 16,
    documentType: 'govId'
  },
  age12Verification: {
    name: 'Age Verification 12+',
    age: 12,
    documentType: 'govId'
  },
  age30Verification: {
    name: 'Age Verification 30+',
    age: 30,
    documentType: 'govId'
  }
};

export const SHIPPING_CARRIERS = [
  { key: 'ups', label: 'UPS' },
  { key: 'usps', label: 'USPS' },
  { key: 'fedex', label: 'FedEx' },
  { key: 'canadapost', label: 'Canada Post' },
  { key: 'shippie', label: 'Shippie' },
  { key: 'logx', label: 'LogX' },
  { key: 'purolator', label: 'Purolator' },
  { key: 'fleetoptics', label: 'Fleet Optics' },
  { key: 'unknown', label: 'Unknown' }
];

export const INSURANCE_TYPES = [
  { key: 'abc', label: 'ABC' },
  { key: 'ahe', label: 'AHE' },
  { key: 'cbp', label: 'CBP' },
  { key: 'cs', label: 'CS' },
  { key: 'esi', label: 'ESI' },
  { key: 'gs', label: 'GS' },
  { key: 'mbc', label: 'MBC' },
  { key: 'mdm', label: 'MDM' },
  { key: 'nihb', label: 'NIHB' },
  { key: 'nx', label: 'NX' },
  { key: 'obc', label: 'OBC' },
  { key: 'odb', label: 'ODB' },
  { key: 'pbc', label: 'PBC' },
  { key: 'pshcp', label: 'PSHCP' },
  { key: 'other', label: 'Other' }
];

export const ORDER_REJECTION_REASONS = [
  { key: 'Patient Not Qualified', label: 'Patient Not Qualified' },
  {
    key: 'Unable to Fulfill Prescription',
    label: 'Unable to Fulfill Prescription'
  },
  {
    key: 'Invalid Prescription Details',
    label: 'Invalid Prescription Details'
  },
  { key: 'Product Out of Stock', label: 'Product Out of Stock' },
  { key: 'Duplicate Order', label: 'Duplicate Order' },
  { key: 'Other', label: 'Other' }
];

export const ORDER_CANCELLATION_REASONS = [
  {
    key: 'Patient Requested Cancellation',
    label: 'Patient Requested Cancellation'
  },
  {
    key: 'Patient Did Not Complete Requirements',
    label: 'Patient Did Not Complete Requirements'
  },
  {
    key: 'Incorrect Medical Information',
    label: 'Incorrect Medical Information'
  },
  { key: 'Payment Failed', label: 'Payment Failed' },
  { key: 'Duplicate Order', label: 'Duplicate Order' },
  { key: 'Order Placed by Mistake', label: 'Order Placed by Mistake' },
  { key: 'Other', label: 'Other' }
];
